/*
 * Copyright (c) 2026 Brainstream Lab — UCogNet Research.
 * Licensed under the Apache License, Version 2.0 (see LICENSE / NOTICE).
 */
import React, { Suspense, lazy } from 'react';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import theme from './theme';
import { useI18n } from './i18n/I18nContext';
import { LightboxProvider } from './components/ImageLightbox';
import ScrollToTop from './components/ScrollToTop';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

/* ── Code-split pages (reduces initial JS bundle) ── */
const HomePage = lazy(() => import('./pages/HomePage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LeadershipPage = lazy(() => import('./pages/LeadershipPage'));

// Octubre 2026: el sitio declara sólo lo que se puede vender y demostrar. Las páginas de investigación,
// benchmarks y aplicaciones se retiraron de la navegación; sus direcciones viejas llevan a la portada para no
// dejar enlaces rotos. Los archivos siguen en el historial de git.
const RETIRADAS = [
  '/how-it-works', '/proof', '/safety', '/research', '/researches', '/researches/:domainSlug', '/updates',
  '/technical-note', '/applications/astrophysics', '/applications/embodied',
  '/applications/continual-adaptation', '/ai-act', '/verify',
];

// Lab / auth / dashboard routes are intentionally NOT wired here.
// They depend on private modules (auth, ProtectedRoute, LabPage, LoginPage,
// DashboardPage) that live outside this public repository.

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<HomePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/leadership" element={<LeadershipPage />} />
        {RETIRADAS.map((p) => (
          <Route key={p} path={p} element={<Navigate to="/" replace />} />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

function LayoutWrapper() {
  return (
    <>
      <Navbar />
      <Suspense fallback={<Box sx={{ minHeight: '100vh', background: '#060B14' }} />}>
        <AnimatedRoutes />
      </Suspense>
      <Footer />
    </>
  );
}

// Reflects the active UI language to the document (<html lang="…">) so crawlers
// and assistive tech know the rendered language.
function HtmlLang() {
  const { lang } = useI18n();
  return <Helmet htmlAttributes={{ lang }} />;
}

function App() {
  return (
    <HelmetProvider>
    <HtmlLang />
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LightboxProvider>
        <Router>
          <ScrollToTop />
          <LayoutWrapper />
        </Router>
      </LightboxProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
